// client/src/components/auth/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../layout/LanguageSelector';

const S = {
  limits: new Map(),
  check(email) {
    const k = email.toLowerCase(), now = Date.now();
    const l = this.limits.get(k) || { c: 0, t: now };
    if (now - l.t > 900000) { this.limits.set(k, { c: 0, t: now }); return true; }
    return l.c < 7;
  },
  fail(email) {
    const k = email.toLowerCase(), l = this.limits.get(k) || { c: 0, t: Date.now() };
    l.c++; this.limits.set(k, l);
  },
  reset(email) { this.limits.delete(email.toLowerCase()); },
  emailOk(email) { 
    return email && email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },
  threat(input) { 
    if (input.indexOf('\x00') !== -1) return true; // Null byte check
    return [
      /<script[^>]*>/i, 
      /<iframe[^>]*>/i, 
      /javascript\s*:/i, 
      /vbscript\s*:/i,
      /on\w+\s*=/i  // Event handlers: onload=, onclick=, etc.
    ].some(p => p.test(input)); 
  },
  clean(input, pwd = false) { 
    if (pwd) return this.threat(input) ? '' : input;
    return input.replace(/<script[^>]*>.*?<\/script>/gi, '').replace(/javascript\s*:/gi, '').trim(); 
  },
  jwt(token) { return token && token.split('.').length === 3; }
};

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState('');
  
  const lastTime = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();

  const { email, password } = formData;
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && S.jwt(token)) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/api/auth/profile', { timeout: 10000 })
        .then((response) => {
          // CORREZIONE: Usa il ruolo dal profilo per navigare correttamente
          const userRole = response.data.user?.role;
          if (userRole) {
            const routes = {
              admin: '/admin/dashboard',
              bank: '/bank/dashboard',
              company: '/company/dashboard'
            };
            navigate(routes[userRole] || '/company/dashboard', { replace: true });
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        });
    }

    if (location.state?.message) {
      const msg = location.state.message;
      if (!S.threat(msg) && msg.length < 300) {
        setInfoMessage(S.clean(msg));
      }
      window.history.replaceState({}, document.title);
    }
  }, [location, navigate]);

  const onChange = e => {
    const { name, value } = e.target;
    
    if (value.length > 500) {
      setWarning(t('auth.forgotPassword.errors.emailTooLong'));
      return;
    }
    
    if (S.threat(value)) {
      setWarning(t('auth.forgotPassword.errors.invalidContent'));
      return;
    }
    
    if (warning) setWarning('');
    setFormData({ ...formData, [name]: S.clean(value, name === 'password') });
    if (error) setError('');
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setWarning('');
    
    if (!email || !password) {
      setError(t('auth.login.errors.fillFields'));
      return;
    }
    
    if (!S.emailOk(email)) {
      setError(t('auth.login.errors.validEmail'));
      return;
    }
    
    if (S.threat(email) || S.threat(password)) {
      setError(t('auth.login.errors.invalidInput'));
      return;
    }
    
    if (!S.check(email)) {
      setError(t('auth.login.errors.tooManyAttempts'));
      return;
    }
    
    const now = Date.now();
    if (now - lastTime.current < 500) {
      setError(t('auth.login.errors.waitMoment'));
      return;
    }
    lastTime.current = now;
    
    setLoading(true);

    try {
      const res = await axios.post('/api/auth/login', {
        email: email.toLowerCase().trim(),
        password
      }, { timeout: 30000 });
      
      if (!res.data?.token || !res.data?.user || !S.jwt(res.data.token)) {
        throw new Error('Invalid response');
      }
      
      if (!['admin', 'bank', 'company'].includes(res.data.user.role)) {
        throw new Error('Invalid role');
      }
      
      S.reset(email);
      await login(res.data.token, res.data.user);
      
      setTimeout(() => {
        const routes = {
          admin: '/admin/dashboard',
          bank: '/bank/dashboard', 
          company: '/company/dashboard'
        };
        navigate(routes[res.data.user.role] || '/company/dashboard'); // CORREZIONE: fallback su company invece di dashboard generico
        setLoading(false);
      }, 100);
      
    } catch (err) {
      S.fail(email);
      
      let msg = t('auth.login.errors.loginFailed');
      if (err.response?.status === 429) msg = t('auth.login.errors.tooManyRequests');
      else if (err.code === 'ECONNABORTED') msg = t('auth.login.errors.timeout');
      else if (!err.response) msg = t('auth.login.errors.networkError');
      
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card auth-card--login">
        <div className="logo-container">
          <img 
            src="/logo512.png" 
            alt="Euracle Logo" 
            className="auth-logo"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
        
        <h2>{t('auth.login.title')}</h2>
        <p className="auth-subtitle">{t('auth.login.subtitle')}</p>
        
        {warning && <div className="alert alert-warning">{warning}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {infoMessage && <div className="alert alert-info">{infoMessage}</div>}
        
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.login.emailLabel')}</label>
            <div className="input-icon-wrapper">
              <span className="input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 6-10 7L2 6" />
                </svg>
              </span>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={onChange}
                maxLength="254"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.login.passwordLabel')}</label>
            <div className="input-icon-wrapper">
              <span className="input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </span>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={onChange}
                maxLength="256"
                autoComplete="current-password"
                required
              />
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block login-btn"
            disabled={loading || !!warning}
          >
            {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            {t('auth.login.forgotPassword')}{' '}
            <Link to="/forgot-password">{t('auth.login.resetHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;